/**
 * Glossary route — plain-English definitions of the poker jargon the
 * coach uses. Browse by category or search. Aimed at newer players who
 * are intimidated by the abbreviations, but useful to anyone.
 *
 * Data + search live in `lib/glossary.ts` (also the source for the
 * phase-2 in-chat term linking).
 */

import { useMemo, useState } from "react";

import {
  CATEGORY_LABELS,
  GLOSSARY,
  groupByCategory,
  searchGlossary,
  type GlossaryTerm,
} from "@/features/study/lib/glossary";

export function Glossary() {
  const [query, setQuery] = useState("");
  const groups = useMemo(
    () => groupByCategory(searchGlossary(query)),
    [query],
  );
  const matchCount = useMemo(
    () => groups.reduce((n, g) => n + g.terms.length, 0),
    [groups],
  );

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-8 space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-text">Glossary</h1>
        <p className="text-sm text-text-muted mt-1">
          Plain-English definitions of the terms the coach uses. {GLOSSARY.length}{" "}
          terms: search or browse by category.
        </p>
      </header>

      <input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search terms, e.g. SPR, c-bet, polarized…"
        className="w-full rounded-lg border border-border bg-surface px-4 py-3 text-sm text-text placeholder:text-text-muted/60 focus:outline-none focus:border-accent/60 transition"
        aria-label="Search the glossary"
      />

      {matchCount === 0 ? (
        <p className="text-sm text-text-muted py-8 text-center">
          No terms match “{query}”.
        </p>
      ) : (
        <div className="space-y-7">
          {groups.map((group) => (
            <section key={group.category}>
              <h2 className="text-xs uppercase tracking-wider text-text-muted mb-2">
                {CATEGORY_LABELS[group.category]}
              </h2>
              <ul className="rounded-xl border border-border bg-surface overflow-hidden divide-y divide-border/50">
                {group.terms.map((t) => (
                  <li key={t.term} className="px-4 py-3">
                    <TermRow term={t} />
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function TermRow({ term }: { term: GlossaryTerm }) {
  return (
    <div>
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="text-sm font-semibold text-text">{term.term}</span>
        {term.expansion && (
          <span className="text-xs text-accent/90">{term.expansion}</span>
        )}
      </div>
      <p className="text-sm text-text-muted mt-1 leading-relaxed">
        {term.definition}
      </p>
    </div>
  );
}
